import { Box, Checkbox, FormControl, TextInput } from '@primer/react';
import { useEffect, useState } from 'react';
import { crmHocSinhApi } from '../../../api/crmHocSinhApi';
import Button from '../../../components/ui/button';
import { useCommonSelectedHook } from '../../../hooks/useCommonSelectedHook';
import { IStudentBaseInformation } from '../../../models/request/acceptance-form/IStudentBaseInformation';
import { crmHocSinhBHYTApi } from '../../../api/crmHocSinhBHYTApi';
import FormBlock from '../profile-form/FormBlock';
import { IHocSinhBHYT, IHocSinhBHYTResponse } from '../../../models/response/crm-hocsinh/IHocSinhBHYTResponse';
import { Controller, useForm } from 'react-hook-form';
import { NotifyHelper } from '../../../helpers/toast';

export interface IBHYTProps {
    ts_hocsinh_id: number;
    canEdit?: boolean;
}

const BHYTForm = (props: IBHYTProps) => {
    const { canEdit = true } = props;
    const [isSaving, setIsSaving] = useState(false);
    const { nam_hoc, dm_coso_id } = useCommonSelectedHook();

    const [formData, setFormData] = useState<IHocSinhBHYT>({
        id: props.ts_hocsinh_id,
        tinh_trang_suc_khoe: '',
        so_dinh_danh: '',
        so_the_bhyt: '',
        noi_dangky_khaisinh: '',
        con_lttv_bodoi_congan: false,
        ghi_chu: '',
    });

    const {
        register,
        handleSubmit,
        clearErrors,
        reset,
        setError,
        control,
        setValue,
        trigger,
        formState: { errors, isValid },
    } = useForm<IHocSinhBHYT>({
        mode: "onChange", // Validate khi người dùng thay đổi input
        reValidateMode: "onChange", // Re-validate khi có thay đổi
        defaultValues: formData,
    });
    useEffect(() => {
        handleGetBHYTData();
    }, [props.ts_hocsinh_id]);

    // useEffect(() => {
    //     if (studentInformations) {
    //         reset(studentInformations, { keepDirty: true, keepTouched: true, keepIsValidating: true });
    //     }
    // }, [studentInformations]);

    const handleGetBHYTData = async () => {
        const res = await crmHocSinhBHYTApi.selectById(props.ts_hocsinh_id);
        if (res.is_success) {
            const data: IHocSinhBHYT = {
                ...res.data,
            };
            setFormData(data);
            reset(data);
        } else {
            NotifyHelper.Error(res.message ?? "Error");
        }
    };

    const onSubmit = async (data: IHocSinhBHYT) => {
        // Validate toàn bộ form trước khi submit
        const validationErrors: any = {};

        if (data.so_the_bhyt === null || data.so_the_bhyt === '') {
            validationErrors.so_the_bhyt = "Vui lòng nhập số thẻ bhyt";
        }

        // Nếu có lỗi validation, hiển thị lỗi và dừng submit
        if (Object.keys(validationErrors).length > 0) {
            Object.keys(validationErrors).forEach(field => {
                setError(field as any, {
                    type: "manual",
                    message: validationErrors[field]
                });
            });
            NotifyHelper.Error("Vui lòng kiểm tra lại thông tin đã nhập");
            return;
        }

        setIsSaving(true);
        try {
            let res = await crmHocSinhBHYTApi.update(data, props.ts_hocsinh_id);

            if (res.is_success) {
                NotifyHelper.Success("Cập nhật lớp thành công");
                // props.onSuccess();
            } else {
                NotifyHelper.Error(res.message ?? "Có lỗi xảy ra khi lưu dữ liệu");
            }
        } catch (error) {
            NotifyHelper.Error("Có lỗi xảy ra khi lưu dữ liệu");
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                    <Box sx={{ fontSize: 16, fontWeight: 600 }}>Thông tin bảo hiểm học sinh:</Box>
                    {canEdit && (
                        <Box>
                            <Button
                                text="Cập nhật"
                                variant="primary"
                                size="medium"
                                type="submit"
                            />
                        </Box>
                    )}
                </Box>
                <div className="row">
                    <div className="col-md-6 mb-3">
                        <FormControl>
                            <FormControl.Label>Tình trạng sức khoẻ</FormControl.Label>
                            <Controller
                                name="tinh_trang_suc_khoe"
                                control={control}
                                rules={{}}
                                render={({ field }) => (
                                    <TextInput
                                        block
                                        disabled={!canEdit}
                                        value={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            clearErrors("tinh_trang_suc_khoe");
                                        }}
                                        placeholder="Nhập tình trạng sức khoẻ"
                                    />
                                )}
                            />
                            {errors.tinh_trang_suc_khoe && (
                                <FormControl.Validation variant="error">
                                    {errors.tinh_trang_suc_khoe.message}
                                </FormControl.Validation>
                            )}
                        </FormControl>
                    </div>
                    <div className="col-md-6 mb-3">
                        <FormControl>
                            <FormControl.Label>Số định danh</FormControl.Label>
                            <Controller
                                name="so_dinh_danh"
                                control={control}
                                rules={{}}
                                render={({ field }) => (
                                    <TextInput
                                        block
                                        disabled={!canEdit}
                                        value={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            clearErrors("so_dinh_danh");
                                        }}
                                        placeholder="Nhập tình số định danh"
                                    />
                                )}
                            />
                            {errors.so_dinh_danh && (
                                <FormControl.Validation variant="error">
                                    {errors.so_dinh_danh.message}
                                </FormControl.Validation>
                            )}
                        </FormControl>
                    </div>
                    <div className="col-md-4 mb-3">
                        <FormControl>
                            <FormControl.Label>Số thẻ BHYT</FormControl.Label>
                            <Controller
                                name="so_the_bhyt"
                                control={control}
                                rules={{
                                    required: "Vui nhập số thẻ BHYT",
                                    validate: (value) => {
                                        if (!value) {
                                            return "Vui nhập số thẻ BHYT";
                                        }
                                        return true;
                                    }
                                }}
                                render={({ field }) => (
                                    <TextInput
                                        block
                                        value={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            clearErrors("so_the_bhyt");
                                        }}
                                        placeholder="Nhập tình số thẻ BHYT"
                                    />
                                )}
                            />
                            {errors.so_the_bhyt && (
                                <FormControl.Validation variant="error">
                                    {errors.so_the_bhyt.message}
                                </FormControl.Validation>
                            )}
                        </FormControl>
                    </div>
                    <div className="col-md-4 mb-3">
                        <FormControl>
                            <FormControl.Label></FormControl.Label>
                            <Controller
                                name="con_lttv_bodoi_congan"
                                control={control}
                                rules={{}}
                                render={({ field }) => (
                                    <>
                                        <div style={{ marginTop: '28px' }}>
                                            <Checkbox
                                                {...field}
                                                value={field.value?.toString()}
                                                checked={field.value ?? false}
                                                sx={{ float: 'left', marginRight: '5px' }}
                                                onChange={(e) => {
                                                    const checked = e.target.checked;
                                                    field.onChange(checked);
                                                }}
                                            />  Con của LLVT - Bộ đội, Công an
                                        </div>

                                    </>

                                )}
                            />
                            {errors.so_the_bhyt && (
                                <FormControl.Validation variant="error">
                                    {errors.so_the_bhyt.message}
                                </FormControl.Validation>
                            )}
                        </FormControl>
                    </div>
                    <div className="col-md-12 mb-12">
                        <FormControl>
                            <FormControl.Label>Nơi đăng ký khai sinh</FormControl.Label>
                            <Controller
                                name="noi_dangky_khaisinh"
                                control={control}
                                rules={{}}
                                render={({ field }) => (
                                    <TextInput
                                        block
                                        value={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            clearErrors("noi_dangky_khaisinh");
                                        }}
                                        placeholder="Nhập tên nơi đăng ký khai sinh"
                                    />
                                )}
                            />
                            {errors.noi_dangky_khaisinh && (
                                <FormControl.Validation variant="error">
                                    {errors.noi_dangky_khaisinh.message}
                                </FormControl.Validation>
                            )}
                        </FormControl>
                    </div>
                    <div className="col-md-12 mb-12">
                        <FormControl>
                            <FormControl.Label>Ghi chú</FormControl.Label>
                            <Controller
                                name="ghi_chu"
                                control={control}
                                rules={{}}
                                render={({ field }) => (
                                    <TextInput
                                        block
                                        value={field.value}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                            clearErrors("ghi_chu");
                                        }}
                                        placeholder="Nhập ghi chú"
                                    />
                                )}
                            />
                            {errors.ghi_chu && (
                                <FormControl.Validation variant="error">
                                    {errors.ghi_chu.message}
                                </FormControl.Validation>
                            )}
                        </FormControl>
                    </div>
                </div>
            </form>
        </Box>
    );
};

export default BHYTForm;
